import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useFlags } from "launchdarkly-react-client-sdk";
import { CALL_DATA, GLOBAL_MEETING_DATA, PublicVariables } from "../../../../constants/PublicVariables";
import { ENABLED, RECORDING, SUPPORT_OPTION } from "../../../../constants/Constants";

import { sendUserDeviceInformation } from "../../../../actions/joinMeetingAction";

import NavigatorService from "../../../../services/navigatorService";
import SupportOption from "../../../../services/supportOption";
import { LogFirebase } from "../../../../services/firestore";

import {
    goToPlayStore,
    isAgentLocationEnable,
    isGuestLocationEnable,
    setExtraFeatureFlags,
    setGlobalMeetingData,
    setImageCompressionValues,
    dualStreamEnabled,
    isOnlyUploadImageAvailable,
} from "../../../../shared/utility";
import { deviceDetailsRequestParams, setPositionError } from "../../../../shared/function";

import { checkGeolocationPermission, getCurrentPosition } from "../../../../shared/geolocationPermission";

import { setCountinueWithWeb, setIsLanguageDropdownBtnVisible, setExpandView, setNqAvailable } from "../../../../reducers/common-reducer";
import { setCameraByCallExperience, setVideoQualityResolution } from "../../CallTopbar/CallSettings/CallSettingUtils";
import { setDataTrack } from "../../../../reducers/twilioEvents-reducer";
import { setRecordingBtnEnabled, setRecordingInfoOnLoad, setRecordingON } from "../../../../reducers/recording-reducer";
import { getRecordingFeatureFlagValue } from "../../../Recording/RecordingUtils";
import { checkUserUpdatedLocation, sendLocationInfo } from "../../../LiveLocation/LocationUtils";
import { handleEndCallFunc } from "../../../Twilio/Room/roomFunction";
import { createMyLocalDataTrack, createMyRoom, getCallConnectParams, showMeRoomTypeIsGoRoomOrP2P } from "../../../Twilio/twilioCallUtility";
import { setLocalTracks } from "../../../Twilio/Room/roomService";
import { handleRoomError, handleRoomSuccess } from "../../../Twilio/Room/roomSuccessErrorFunction";
import { useTwilioRoom } from "../../../../providers/useTwilioRoomProvider";
import takePhotoService from "../../../../services/takePhotoService";

const JoinMeeting = () => {
    const { joinMeetingData } = useSelector((state) => state.joinMeeting);
    const { countinueWithWeb } = useSelector((state) => state.commonReducer);
    const { batteryValue, batteryState } = useSelector((state) => state.batteryNetworkInfo);
    const { mediate } = useSelector((state) => state.silentObserver);
    const dispatch = useDispatch();
    const ldFlags = useFlags();
    const { setRoom } = useTwilioRoom();

    const connectToRoom = useCallback(async (token, videoParams) => {
        createMyRoom(token, videoParams).then(
            function (room) {
                setRoom(room); // Call the setRoom function to create a room and ensure the updated value is propagated throughout the application using the provider.
                handleRoomSuccess(room, joinMeetingData, ldFlags);
                if (ldFlags.captureDeviceAndGoogleEvent) {
                    handleDeviceInformation(); // send user device information
                }
            },
            function (error) {
                handleRoomError(error, joinMeetingData);
            }
        );
    }, []);

    const handleFetchUserLocationPermission = useCallback(() => {
        if (isGuestLocationEnable(joinMeetingData) || isAgentLocationEnable(joinMeetingData)) {
            checkGeolocationPermission()
                .then(function () {
                    return getCurrentPosition();
                })
                .then(function (position) {
                    // Handle successful position retrieval
                    setPositionSuccess(position);
                })
                .catch(async function (error) {
                    // Handle errors
                    console.error("Error getting current position:", error);
                    setPositionError(error);
                });
        }
    }, [joinMeetingData]);

    const allowUserToRoomConnect = useMemo(() => {
        return (
            GLOBAL_MEETING_DATA.smsSupportOption == SUPPORT_OPTION.BROWSER_FIRST ||
            SupportOption.isBrowserFirst(joinMeetingData) ||
            SupportOption.isBrowserOnly(joinMeetingData) ||
            countinueWithWeb ||
            GLOBAL_MEETING_DATA.isAgent ||
            !NavigatorService.isMobileDevice()
        );
    }, [countinueWithWeb]);

    const startJoiningCall = useCallback((joinMeetingData) => {
        const localTracksArray = [];
        const dataTrack = createMyLocalDataTrack(); // create twilio local data track

        dispatch(setDataTrack(dataTrack));
        setLocalTracks();
        localTracksArray.push(dataTrack);

        let videoParams = {
            ...getCallConnectParams(joinMeetingData),
            tracks: localTracksArray,
        };

        console.log("videoParams: ", videoParams);
        connectToRoom(joinMeetingData?.twilioToken, videoParams, CALL_DATA.memberId); // handle call connect using twilio SDK
    }, []);

    useEffect(() => {
        if (joinMeetingData?.sessionConfigurations) {
            PublicVariables.featureFlag = joinMeetingData?.sessionConfigurations; // set feature flag in Public variable
            PublicVariables.isOnlyUploadImageMeet = isOnlyUploadImageAvailable();

            setExtraFeatureFlags(joinMeetingData?.sessionConfigurations);

            dispatch(setExpandView(!dualStreamEnabled())); // Setting Expand View
            dispatch(setNqAvailable(!showMeRoomTypeIsGoRoomOrP2P(joinMeetingData?.sessionConfigurations)));
            if (GLOBAL_MEETING_DATA.isAgent) {
                CALL_DATA.participantUserName = joinMeetingData?.ownerName;
            }

            setGlobalMeetingData(joinMeetingData); // set meeting data in global variable

            const imageCompressionData = JSON.parse(joinMeetingData?.imageCompression);
            setImageCompressionValues(imageCompressionData); // set image compression data
            setVideoQualityResolution(joinMeetingData);
            // sendDeviceLocationInfo
            if (GLOBAL_MEETING_DATA.isAgent === true) {
                GLOBAL_MEETING_DATA.smsSupportOption = SUPPORT_OPTION.BROWSER_FIRST; // if agent is there then always user connect through the Web
            } else {
                console.log("do nothing");
            }
            if (window.navigator?.language) {
                LogFirebase("Language", "browser_original", window.navigator.language);
            }
            LogFirebase("Language", "current_language", PublicVariables.currentLanguageObj.languageCode);
            if (GLOBAL_MEETING_DATA.isAgent) {
                LogFirebase("AVAILABLE_IS_AGENT", "true", "");
            } else if (CALL_DATA.isObserver && !mediate) {
                LogFirebase("AVAILABLE_IS_OBSERVER", "true", "");
            } else {
                LogFirebase("AVAILABLE_IS_GUEST", "true", "");
            }
            LogFirebase("FeatureFlags", "FeatureFlagList", PublicVariables.featureFlag);
            CALL_DATA.recordingRoomKey = joinMeetingData?.recordingRoom;
            if (getRecordingFeatureFlagValue() == RECORDING.ON) {
                // on join recording started
                dispatch(setRecordingInfoOnLoad(true));
                dispatch(setRecordingON(true));
                CALL_DATA.roomName = joinMeetingData?.recordingRoom;
            } else if (getRecordingFeatureFlagValue() == RECORDING.INITOFF) {
                // recording start/stop using button
                dispatch(setRecordingBtnEnabled(true));
                CALL_DATA.roomName = joinMeetingData?.normalRoom;
            } else {
                CALL_DATA.roomName = joinMeetingData?.normalRoom;
            }
            LogFirebase("Recording", "option", PublicVariables.featureFlag?.SHOWMERECORD);

            /*
                - Check support option from the feature flag for the mobile and Desktop
                - If is app only option is set from the CP, then redirect go to playstore for mobile device
                - if Desktop browser is there then do not check anything
            */
            if (NavigatorService.isMobileDeviceOrIpadOs()) {
                if (SupportOption.isAppOnly(joinMeetingData)) {
                    handleEndCallFunc();
                    goToPlayStore(); //use APP only
                } else if (SupportOption.isAskUser(joinMeetingData)) {
                    // Ask end User
                    // To restrict "ask user" popup
                    dispatch(setCountinueWithWeb(true));
                }
            }

            //for desktop and when user selects browser in pop up
            if (allowUserToRoomConnect || (!GLOBAL_MEETING_DATA.isAgent && !countinueWithWeb && SupportOption.isAskUser(joinMeetingData))) {
                handleFetchUserLocationPermission(); // fetch user location
                dispatch(setIsLanguageDropdownBtnVisible(false)); // once getting join api success than hiding change language dropdown
                // handle upload only view (no need to join twilio SDK call)
                if (PublicVariables.isOnlyUploadImageMeet) {
                    // handle upload only flow from here
                    console.log("handle upload only flow from here");
                } else {
                    console.log(joinMeetingData, "joinMeetingData");
                    setCameraByCallExperience();
                    takePhotoService.takePhotoresetState();
                    startJoiningCall(joinMeetingData);
                }
            }
        }
    }, [joinMeetingData?.sessionConfigurations]);

    const handleDeviceInformation = async () => {
        let requestData = {
            sessionId: GLOBAL_MEETING_DATA.sessionId,
            memberId: CALL_DATA.memberId,
        };
        requestData = deviceDetailsRequestParams(requestData);
        requestData.batteryState = batteryState;
        requestData.batteryValue = batteryValue;
        const details = {
            batteryStatus: batteryState,
            batteryLevel: batteryValue,
        };
        LogFirebase("DEVICE_INFORMATION", "INFORMATION", requestData);
        dispatch(sendUserDeviceInformation(requestData, details));
    };

    const setPositionSuccess = async (position) => {
        console.log("Geolocation successful:", position);
        LogFirebase("PERMISSION", "LOCATION_PERMISSION", "granted");

        PublicVariables.allowedLocation = true;
        CALL_DATA.Latitude = position.coords.latitude;
        CALL_DATA.Longitude = position.coords.longitude;

        if (!GLOBAL_MEETING_DATA.isAgent && joinMeetingData?.sessionConfigurations.ENABLELOCATION === ENABLED) {
            // send location data track for guest and slient observer
            /* 207 notification for sending */
            setTimeout(() => {
                sendLocationInfo({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    diff: 0,
                });
            }, 2000);
            if (GLOBAL_MEETING_DATA.isGuest && checkUserUpdatedLocation()); // send guest updated location on every 10 seconds
        }

        LogFirebase("LOCATION", "LATITUDE", position.coords.latitude);
        LogFirebase("LOCATION", "LONGITUDE", position.coords.longitude);
    };

    return <></>;
};

export default JoinMeeting;
